import { supabase } from '@/integrations/supabase/client';

export interface StarterRecipe {
  id: string;
  title: string;
  description: string | null;
  ingredients: string[];
  instructions: string[];
  prep_time: string | null;
  cook_time: string | null;
  total_time: string | null;
  servings: string | null;
  source_url: string | null;
  image_url: string | null;
  storage_image_url: string | null;
  storage_image_path: string | null;
  tags: string[] | null;
  structured_ingredients_metric: any[] | null;
  structured_ingredients_imperial: any[] | null;
  imperial_version: any | null;
  metric_version: any | null;
  base_measurement_system: string | null;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface StarterRecipeInput {
  title: string;
  description?: string | null;
  ingredients?: string[];
  instructions?: string[];
  prep_time?: string | null;
  cook_time?: string | null;
  total_time?: string | null;
  servings?: string | null;
  source_url?: string | null;
  image_url?: string | null;
  storage_image_url?: string | null;
  storage_image_path?: string | null;
  tags?: string[] | null;
  structured_ingredients_metric?: any[] | null;
  structured_ingredients_imperial?: any[] | null;
  imperial_version?: any | null;
  metric_version?: any | null;
  base_measurement_system?: string | null;
  is_active?: boolean;
  display_order?: number;
}

export const starterRecipeAdminService = {
  /**
   * Fetch all starter recipes (including inactive) for admin management
   */
  async getAllStarterRecipes(): Promise<StarterRecipe[]> {
    console.log('Fetching all starter recipes for admin...');
    
    const { data, error } = await supabase.functions.invoke('manage-starter-recipes', {
      body: { action: 'list' }
    });

    if (error) {
      console.error('Error fetching starter recipes:', error);
      throw new Error(`Failed to fetch starter recipes: ${error.message}`);
    }

    if (data?.error) {
      console.error('Starter recipes error:', data.error);
      throw new Error(data.error);
    }

    console.log('Fetched starter recipes:', data?.recipes?.length || 0);
    return data?.recipes || [];
  },

  /**
   * Create a new starter recipe from scraped data
   */
  async createStarterRecipe(recipe: StarterRecipeInput): Promise<StarterRecipe> {
    console.log('Creating starter recipe:', recipe.title);
    
    const { data, error } = await supabase.functions.invoke('manage-starter-recipes', {
      body: { action: 'create', recipe }
    });

    if (error) {
      console.error('Error creating starter recipe:', error);
      throw new Error(`Failed to create starter recipe: ${error.message}`);
    }

    if (data?.error) {
      console.error('Create starter recipe error:', data.error);
      throw new Error(data.error);
    }

    console.log('Created starter recipe:', data?.recipe?.id);
    return data?.recipe;
  },

  /**
   * Update an existing starter recipe
   */
  async updateStarterRecipe(recipeId: string, updates: Partial<StarterRecipeInput>): Promise<StarterRecipe> {
    console.log('Updating starter recipe:', recipeId);
    
    const { data, error } = await supabase.functions.invoke('manage-starter-recipes', {
      body: { action: 'update', recipeId, recipe: updates }
    });

    if (error) {
      console.error('Error updating starter recipe:', error);
      throw new Error(`Failed to update starter recipe: ${error.message}`);
    }

    if (data?.error) {
      console.error('Update starter recipe error:', data.error);
      throw new Error(data.error);
    }

    console.log('Updated starter recipe:', data?.recipe?.id);
    return data?.recipe;
  },

  /**
   * Delete a starter recipe
   */
  async deleteStarterRecipe(recipeId: string): Promise<void> {
    console.log('Deleting starter recipe:', recipeId);
    
    const { data, error } = await supabase.functions.invoke('manage-starter-recipes', {
      body: { action: 'delete', recipeId }
    });

    if (error) {
      console.error('Error deleting starter recipe:', error);
      throw new Error(`Failed to delete starter recipe: ${error.message}`);
    }

    if (data?.error) {
      console.error('Delete starter recipe error:', data.error);
      throw new Error(data.error);
    }

    console.log('Deleted starter recipe:', recipeId);
  },

  /**
   * Toggle the active status of a starter recipe
   */
  async toggleActive(recipeId: string, isActive: boolean): Promise<StarterRecipe> {
    console.log('Toggling starter recipe active:', recipeId, '->', isActive);
    
    const { data, error } = await supabase.functions.invoke('manage-starter-recipes', {
      body: { action: 'toggle_active', recipeId, isActive }
    });

    if (error) {
      console.error('Error toggling starter recipe:', error);
      throw new Error(`Failed to toggle starter recipe: ${error.message}`);
    }

    if (data?.error) {
      console.error('Toggle starter recipe error:', data.error);
      throw new Error(data.error);
    }

    console.log('Toggled starter recipe:', data?.recipe?.id, 'is_active:', data?.recipe?.is_active);
    return data?.recipe;
  },

  /**
   * Scrape a recipe from URL using the existing scrape-recipe function
   */
  async scrapeRecipeFromUrl(url: string): Promise<any> {
    console.log('Scraping recipe from URL:', url);
    
    const { data, error } = await supabase.functions.invoke('scrape-recipe', {
      body: { url }
    });

    if (error) {
      console.error('Error scraping recipe:', error);
      throw new Error(`Failed to scrape recipe: ${error.message}`);
    }

    if (data?.error) {
      console.error('Scrape recipe error:', data.error);
      throw new Error(data.error);
    }

    console.log('Scraped recipe:', data?.recipe?.title);
    return data?.recipe;
  }
};
